import React from 'react';
import NumberFormat from 'react-number-format';
import { formatPrice } from 'lib/pricing/ui';
import { firestoreTimestampOrDateToDate, getCurrencySymbol } from 'lib/helpers';
import { ESnapshotExists, ERef } from 'lib/types';
import { ERate } from 'lib/types/rates';
import { shouldDistributeFee, getInvoiceAmountsBreakdown } from 'lib/pricing';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import {
  LineItem,
  OrderInvoice,
  PublicNoticeInvoice
} from 'lib/types/invoices';
import moment from 'moment';
import InvoiceFormTable from 'routes/notice/billing/oldCreateInvoice/InvoiceFormTable';
import { GridInput } from 'lib/components/Card/Grid';
import { DistributeSettings } from 'lib/types/organization';

interface InvoiceRefundModalNoticeTableProps {
  rate: ERef<ERate>;
  invoice: ESnapshotExists<OrderInvoice> | ESnapshotExists<PublicNoticeInvoice>;
  currency: string;
  timezone: string;
  baseDistributeFeeSetting?: DistributeSettings;
  refunds?: LineItem[] | null | undefined;
  totalRefundedInCentsFromPriorInvoiceTransactions: number;
  lineItemsDiff: LineItem[];
  invoiceLineItems: LineItem[];
}

export function InvoiceRefundModalNoticeTable({
  rate,
  invoice,
  currency,
  timezone,
  baseDistributeFeeSetting,
  refunds,
  totalRefundedInCentsFromPriorInvoiceTransactions,
  lineItemsDiff,
  invoiceLineItems
}: InvoiceRefundModalNoticeTableProps) {
  const currencySymbol = getCurrencySymbol(currency);
  const hasRefunds = refunds && refunds.length > 0;

  const { value: rateSnapshot } = useAsyncEffect({
    fetchData: () => rate.get(),
    dependencies: [rate?.id]
  });

  const taxPercent = invoice.data().inAppTaxPct;
  const distributeFeeSetting =
    rateSnapshot?.data()?.distributeEnoticeFee || baseDistributeFeeSetting;
  const isFeeDistributed = shouldDistributeFee(distributeFeeSetting);
  const {
    subtotalInCents,
    totalInCents: totalDueInCents,
    columnAmountInCents,
    placementFeeInCents,
    applicationFeeInCents,
    noticeAddOnsInCents
  } = getInvoiceAmountsBreakdown(invoice);
  /**
   * Application fee is a transfer withholding, not an addition to the invoice
   * Placement fee is a charge to the demand-side advertiser org, and is not counted in 'columnAmountInCents',
   * which is historically the amount the publisher owes to Column
   * Add-ons are Column revenue but are already itemized in the line item table above
   */
  const columnFeesChargesOnInvoice =
    columnAmountInCents -
    applicationFeeInCents -
    noticeAddOnsInCents +
    placementFeeInCents;
  return (
    <>
      <GridInput fullWidth>
        <InvoiceFormTable
          values={invoiceLineItems}
          diffValues={{
            invoicePubLineItemDiffValues: lineItemsDiff,
            noticePubDateDiffValues: null
          }}
          currency={currencySymbol}
          handleChange={() => {}}
          preventPricingModification
        />
      </GridInput>
      <GridInput fullWidth>
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:p-0">
            <dl>
              <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                <dt className="text-sm leading-5 font-bold text-column-gray-500">
                  Subtotal
                </dt>
                <dd className="mt-1 text-right text-sm leading-5 text-column-gray-600 sm:mt-0 sm:col-span-2">
                  <NumberFormat
                    value={formatPrice(currencySymbol, subtotalInCents)}
                    thousandSeparator
                    prefix={currencySymbol}
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="text"
                  />
                </dd>
              </div>
              {columnFeesChargesOnInvoice > 0 && (
                <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-sm leading-5 font-bold text-column-gray-500">
                    Column Fees {isFeeDistributed ? ' (Embedded)' : ''}
                  </dt>
                  <dd className="mt-1 text-right text-sm leading-5 text-column-gray-600 sm:mt-0 sm:col-span-2">
                    <NumberFormat
                      value={formatPrice(
                        currencySymbol,
                        columnFeesChargesOnInvoice
                      )}
                      thousandSeparator
                      prefix={currencySymbol}
                      decimalScale={2}
                      fixedDecimalScale
                      displayType="text"
                    />
                  </dd>
                </div>
              )}
              <div className="mt-8 sm:mt-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-t sm:border-column-gray-50 sm:px-6 sm:py-5">
                <dt className="text-sm leading-5 font-bold text-column-gray-500">
                  Tax
                </dt>
                <dd className="mt-1 text-right text-sm leading-5 text-column-gray-600 sm:mt-0 sm:col-span-2">
                  <NumberFormat
                    placeholder={`${taxPercent || '0.00'}`}
                    value={taxPercent}
                    thousandSeparator
                    suffix="%"
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="text"
                  />
                </dd>
              </div>
              <div>
                {refunds &&
                  refunds.length > 0 &&
                  refunds.map((lineItem, index) => {
                    const revisedDate = firestoreTimestampOrDateToDate(
                      lineItem.date
                    );
                    const dateString =
                      moment(revisedDate).tz(timezone).format('MM-DD-YY') ?? '';
                    return (
                      <div
                        className="sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5"
                        key={index}
                      >
                        <dt className="text-sm leading-5 font-bold text-column-gray-500 sm:col-span-2">
                          Refund {index + 1}: {lineItem.description}{' '}
                          {lineItem.date ? `- initiated ${dateString}` : ''}
                        </dt>
                        <dd className="mt-1 text-right text-sm leading-5 text-column-gray-600 sm:mt-0 sm:col-span-1">
                          <NumberFormat
                            value={formatPrice(
                              currencySymbol,
                              -1 * lineItem.amount
                            )}
                            thousandSeparator
                            prefix={currencySymbol}
                            decimalScale={2}
                            fixedDecimalScale
                            displayType="text"
                          />
                        </dd>
                      </div>
                    );
                  })}
              </div>
              <div className="mt-8 sm:mt-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:border-t sm:border-column-gray-50 sm:px-6 sm:py-5 bg-column-green-50">
                <dt className="text-sm leading-5 font-bold text-column-green-500">
                  {hasRefunds ? 'Net Total Paid' : 'Total Paid'}
                </dt>
                <dd className="mt-1 text-right text-sm leading-5 text-column-green-500 sm:mt-0 sm:col-span-2">
                  <NumberFormat
                    placeholder={`${currencySymbol}0.00`}
                    value={formatPrice(
                      currencySymbol,
                      totalDueInCents -
                        totalRefundedInCentsFromPriorInvoiceTransactions
                    )}
                    thousandSeparator
                    prefix={currencySymbol}
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="text"
                  />{' '}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </GridInput>
    </>
  );
}
